import { FormProvider } from 'react-hook-form';
import { ComponentProps } from 'react';
import {
    Box, Flex, ModalBody, Spinner,
} from '@chakra-ui/react';
import { useProperty, usePropertyKeyPickupLocation } from '../../hooks/use-property';
import { EditIntegratedPropertyScreen } from './edit-integrated-property-screen';
import { useUpdateIntegratedPropertyForm } from '../../hooks/use-update-integrated-property-form';
import { ConfirmIntegratedPropertyEditScreen } from './confirm-integrated-property-screen';
import { ReadOnlyPropertyScreen } from './read-only-property-screen';

export function EditIntegratedPropertyForm({
    propertyId,
    onClose,
    onUpdatedProperty,
    showHeader = true,
    showCloseButton = true,
    p,
    confirmButtonText,
    showAgencyKeyFields = true,
    showOnsiteKeyFields = true,
    isReadOnly = false,
}: {
    propertyId: number;
    confirmButtonText?: string;
    onClose: () => void;
    onUpdatedProperty?: () => void;
    showHeader?: boolean;
    showCloseButton?: boolean;
    p?: ComponentProps<typeof ModalBody>['p'];
    showAgencyKeyFields?: boolean;
    showOnsiteKeyFields?: boolean;
    isReadOnly?: boolean;
}) {
    const {
        screen, editPropertyForm, updatePropertyDetailsMutation,
        handleSubmit, handleConfirm, handleBack, handleClose,
    } = useUpdateIntegratedPropertyForm({
        propertyId,
        onClose,
        onUpdatedProperty,
        isReadOnly,
    });

    const propertyQuery = useProperty(propertyId);
    const usePickupLocationQuery = usePropertyKeyPickupLocation(propertyId);

    if (
        propertyQuery.data === undefined
        || propertyQuery.isLoading
        || usePickupLocationQuery.data === undefined
        || usePickupLocationQuery.isLoading
    ) {
        return (
            <ModalBody p={p}>
                <Box w="full">
                    <Flex
                        alignItems="center"
                        fontWeight="bold"
                        justifyContent="center"
                        py={8}
                        px={8}
                        borderColor="muted.50"
                        borderWidth={1}
                        rounded="md"
                    >
                        <Spinner />
                    </Flex>
                </Box>
            </ModalBody>
        );
    }
    return (
        <FormProvider {...editPropertyForm}>
            {screen === 'READ_ONLY' && (
                <ReadOnlyPropertyScreen
                    propertyId={propertyId}
                    onClose={onClose}
                    showHeader={showHeader}
                    showCloseButton={showCloseButton}
                    p={p}
                    showAgencyKeyFields={showAgencyKeyFields}
                    showOnsiteKeyFields={showOnsiteKeyFields}
                />
            )}
            {screen === 'EDIT_PROPERTY_DATA' ? (
                <EditIntegratedPropertyScreen
                    propertyId={propertyId}
                    propertyManager={propertyQuery.data.propertyManager}
                    keyNumber={propertyQuery.data.keyNumber}
                    keyLocationId={usePickupLocationQuery.data.keyLocationId}
                    isKeyAvailable={propertyQuery.data.isKeyAvailable}
                    keyPickupLocationFullAddress={usePickupLocationQuery.data.fullAddress}
                    onSubmit={handleSubmit}
                    onClose={handleClose}
                    showHeader={showHeader}
                    showCloseButton={showCloseButton}
                    p={p}
                    showAgencyKeyFields={showAgencyKeyFields}
                    showOnsiteKeyFields={showOnsiteKeyFields}
                />
            ) : null}
            {screen === 'CONFIRM' ? (
                <ConfirmIntegratedPropertyEditScreen
                    propertyId={propertyId}
                    onSubmit={handleConfirm}
                    onBack={handleBack}
                    error={updatePropertyDetailsMutation.error}
                    showHeader={showHeader}
                    p={p}
                    confirmButtonText={confirmButtonText}
                    showAgencyKeyFields={showAgencyKeyFields}
                    showOnsiteKeyFields={showOnsiteKeyFields}
                />
            ) : null}
        </FormProvider>
    );
}
